// R5.3 — LAYER 1: THE FACT LEDGER, AND THE §3 CONFIRMATION SCREEN (spec §4.1, §5, §3, §1.5).
//
// ONE COMPONENT, TWO MODES, because they are the same surface at two points in the journey and shipping them as
// two components would mean two answers to every question below.
//
//   `confirm`  The §3 confirmation screen. Reached after extraction and BEFORE the state-N gated GET, so no read
//              exists yet and every correction posted from here is §5 step 0 — LEDGER-ONLY. "The confirm step is
//              skippable ('Looks right — run the review') but NEVER HIDDEN" (§3): the screen always renders, and
//              `CS-CONFIRM-ENTRY-CTA` is how the applicant leaves it. Since round 2 the SERVER enforces the same
//              thing — a state-N GET without the proceed marker answers `409 needs_confirmation` — so the screen
//              survives a reload instead of being replaced by the review it exists to precede.
//   `read`     Layer 1 inside the review surface. A current read exists, so every correction here recomputes,
//              free and instantly (R5-OQ-A), and the diff rides above the panel (`BandChangeDiff`).
//
// ─── WHAT ROUND 2 CHANGED, AND WHY (build-review finding 4) ───────────────────────────────────────────────────
//
// Round 1 rendered a fact ONLY when it had applicant prose to show, and offered an editor for five free-text
// fields. Both were consequences of one thing: Appendix A registered no field labels and no enum-value labels, so
// a fact whose typed value is a date, a count, a boolean or a closed §2 enum had nothing this surface was allowed
// to say about it. The review rejected that — §4.1 requires each fact's TYPED VALUE, and codebook §2.1 rule 5 says
// the user can edit ANY fact — so the missing copy is registered (Appendix A.5-P) and resolved through
// `factLabels.ts`, and this panel now renders and edits every fact class:
//
//   * every fact renders `<field label>: <typed value>` — enum members through their registered labels, dates and
//     counts through the registered single-slot value templates, booleans as Yes/No, absence as "Not stated";
//   * every editable field gets an editor CHOSEN BY KIND — textarea for the one multi-line clause field, select
//     for enums and booleans, a number control for counts, a date-partial control for `YYYY`/`YYYY-MM`, text
//     otherwise — with an explicit "Not stated" option wherever §2's schema admits one;
//   * validation is WORDED, visible, and programmatically associated (`aria-errormessage`), with focus returned
//     to the control that has the problem;
//   * `Save` and a visible `Cancel` are real controls rather than a bare submit and an undiscoverable ESC.
//
// ─── EVIDENCE IS SHOWN ONLY WHERE THERE IS EVIDENCE (finding 4) ───────────────────────────────────────────────
//
// Round 1 gave every rendered fact a `CS-SHOW-SOURCE` reveal containing its CURRENT value — including a
// `user_supplied` fact with no `replaces_span`, whose current value is something the applicant typed and never
// appeared in their CV at all. "Shown from your CV" is a provenance CLAIM, and making it about a value that has
// no span is a false one. The reveal now renders for exactly two cases: an `extracted_span` fact (its own span),
// and a `user_supplied` fact carrying a genuine `replaces_span` (the original wording, still available). Anything
// else offers no reveal, which is this surface's standing rule for evidence it cannot resolve.
//
// ─── §5's FOUR RATIFIED CONTROLS, PLUS THE THREE A.5-P ADDS ───────────────────────────────────────────────────
//
// Ratified (A.5): `CS-CORRECT-ACTION` ("Edit"), `CS-FLAG-WRONG-ACTION` ("This isn't right"), `CS-FLAG-WRONG-UNDO`
// ("Undo — this was correct"), `CS-CONFIRM-ENTRY-CTA` ("Looks right — run the review", the screen-level proceed).
// Provisional (A.5-P): `CS-CONFIRM-ENTRY-ACTION` ("Confirm this entry" — §5's third correction class, which had
// no control at all), `CS-EDIT-SAVE`, `CS-EDIT-CANCEL`.
//
// EVERY REPEATED CONTROL HAS A UNIQUE ACCESSIBLE NAME, and it is built from registered copy plus the applicant's
// own words rather than from an invented disambiguator: the registered action label, then the registered FIELD
// label, then the applicant's value, the last two in a visually-hidden span. Two "Edit" buttons on one card are
// therefore "Edit Role title Small-group tutor" and "Edit Start date 2022-01" to a screen reader.
//
// ─── §1.5's INERT BOUNDARY, CLIENT HALF ───────────────────────────────────────────────────────────────────────
//
//   * Every applicant string renders through `ApplicantText` — a React TEXT NODE, never `dangerouslySetInnerHTML`,
//     never an attribute, id, href, class or style value. The only things this file puts in attributes are system
//     ids and closed enum names (`data-entry-id`, `data-fact-state`, `data-confirmation-state`), which §1.5 rule 2
//     permits by name. The ONE exemption is the active controlled editor's own `.value`, which React mirrors onto
//     the attribute and which no text field can avoid; `FactLedgerPanel.test.tsx`'s enumerating sentinel pins it
//     to exactly that attribute on exactly that element.
//   * Every edit is NFC-normalized, newline-normalized and length-bounded BEFORE it is sent, through `factEdit.ts`
//     — the same module the server validates with — so the field shows the string that will actually be stored.
import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { COPY, type CopyStringId } from './copy';
import { ApplicantText, Copy, EvidenceReveal, LayerHeading, layerHeadingId, ScreenReaderOnly } from './renderContent';
import { applicantTextOf, entryHeadingOf, type EntryHeading } from './evidenceIndex';
import { EntryLabel } from './EntryLabel';
import {
  canSubmit, coerceEditValue, maxLengthFor, prepareEdit, NOT_STATED, UI_EDITABLE_FIELDS,
  type Correction, type CorrectionState,
} from './corrections';
// THE ONE last-wins resolver over an append-only `facts[]` — shared with the rubric engine and the PATCH route
// (round 2, finding 5). A second implementation here would be a second opinion about which fact is current.
import { terminalFactsByField } from '../../../workers/src/lib/terminalFacts';
import { type FieldPresentation, editableValues, enumLabelFor, presentationFor } from '../../../workers/src/lib/factLabels';
import {
  BODY_STYLE, CARD_HEAD_STYLE, ENTRY_CARD_STYLE, ENTRY_TITLE_STYLE, FACTS_DETAILS_STYLE, FACTS_SUMMARY_STYLE,
  MUTED_STYLE, NOTICE_STYLE, SECTION_STYLE,
} from './styles';

/* ============================================================================================================== *
 * THE LEDGER SHAPES, STRUCTURALLY TYPED
 *
 * `GET /api/fact-ledger/:cv_id` is read defensively here for the same reason `LegacyStoredView` reads its payload
 * defensively: a malformed row must cost an affordance, never a render.
 * ============================================================================================================== */

export interface LedgerFactLike {
  fact_id?: unknown;
  field?: unknown;
  value?: unknown;
  provenance?: unknown;
  evidence_span?: { quoted_text?: unknown } | null;
  replaces_span?: { quoted_text?: unknown } | null;
  user_value?: unknown;
  user_flagged_wrong?: unknown;
}

export interface LedgerEntryLike {
  entry_id?: unknown;
  role_title?: unknown;
  /** R5.7 — the other two rungs of the entry label's fallback, and its dates (`entryHeadingOf`). */
  institution?: unknown;
  raw_text?: unknown;
  date_start?: unknown;
  date_end?: unknown;
  confirmation_state?: unknown;
  extraction_uncertain?: unknown;
  facts?: unknown;
}

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim().length > 0 ? v : null);

/**
 * §2.8's FIVE rendered states → their five registered chip labels.
 *
 * `user_declined_to_confirm` joined the table in round 2 (finding 4). It is one of §2.8's five states, the server
 * accepts the transition, and the applicant who chose it was being told nothing at all about a state they had
 * deliberately entered — the chip said "Not yet reviewed", which is what `extracted` means and is a different
 * claim. `CS-FACT-DECLINED` is its own registered string (A.5-P).
 */
const STATE_CHIP: Readonly<Record<string, CopyStringId>> = Object.freeze({
  // 2026-08-23 — WHAT THIS CHIP ACTUALLY MEANS, SAID IN WORDS THAT ARE TRUE (owner verdict on a live read: "state
  // semantics still wrong — fix for real"). `extracted` is §2.8's name for "the APPLICANT has not acted on these
  // facts" — it is a CONFIRMATION state, and it says nothing about whether the engine reviewed the entry. The
  // ratified chip for it reads "Not yet reviewed" (`CS-FACT-UNCONFIRMED`), which on a finished review surface,
  // beside a CV reviewed minutes earlier, states something false about the product's own work. The registered
  // replacement `CS-FACT-UNCONFIRMED-ENTRY` ("You haven't confirmed these facts yet") names the applicant's
  // outstanding action instead. `CS-FACT-UNCONFIRMED` stays registered and byte-identical (Appendix A.5, ratified);
  // this is one id→id substitution at one call site, exactly like `CS-CONF-MODERATE` → `CS-CONF-MODERATE-ENTRY`.
  extracted: 'CS-FACT-UNCONFIRMED-ENTRY',
  user_confirmed: 'CS-FACT-CONFIRMED',
  user_edited: 'CS-FACT-EDITED',
  user_flagged_wrong: 'CS-FACT-FLAGGED',
  user_declined_to_confirm: 'CS-FACT-DECLINED',
});

const SIGNAL_PREFIX = 'signal.';

/* ============================================================================================================== *
 * WHAT THE DISCLOSURE SHOWS — POSITIVE FACTS, PLUS THE FEW FIELDS THE APPLICANT CAN CORRECT (2026-08-23)
 *
 * THE OWNER'S VERDICT ON A LIVE READ, verbatim: "~25 internal boolean facts ('States a collaborative function No',
 * 'Assigned to Clinical experience No'…). Never show unset/No internals; the disclosure shows only positive facts
 * + the 2-3 editable fields (entry type, dates, status) with corrections." And, standing: "that seems like a raw
 * database."
 *
 * THE RULE. A fact whose value is NEGATIVE OR UNSET — `No`, `not_stated`, `null`, `false`, an empty string —
 * renders NOTHING, unless its field is one of `ALWAYS_SHOWN_FIELDS` below.
 *
 * WHY THIS IS NOT HIDING ANYTHING. §2.2a's signal facts are a CLOSED CHECKLIST the extractor walks for every
 * entry: a `false` on one of them is not an observation about the applicant's CV, it is the absence of an
 * observation — the checklist item simply did not fire. Twenty-five "No"s in a row are the checklist, printed.
 * §2.1 rule 2 already forbids rendering an absence as a deficiency, and rendering it as `Label: No` is exactly
 * that, twenty-five times. Nothing that the engine USED is suppressed: every band, level, flag and recommendation
 * on the surface is unchanged, and a fact that fired still renders with its value, its source reveal and its
 * editor.
 *
 * WHY THE FOUR EXCEPTIONS ARE EXCEPTIONS. `entry_type`, `date_start`, `date_end`, `is_ongoing` and
 * `activity_status` are the fields the applicant is being ASKED to check — the ones the owner named. An unset
 * date is the most correctable thing on the card, and suppressing it would remove the editor along with the row.
 * So these render even when unset, as "Not stated" with their Edit control, which is the affordance, not noise.
 * ============================================================================================================== */

/**
 * The fields that render even when unset, because their editor is the point — "the 2-3 editable fields (entry
 * type, dates, status)". Every member is in `UI_EDITABLE_FIELDS`, asserted in `FactLedgerPanel.test.tsx`.
 */
export const ALWAYS_SHOWN_FIELDS: readonly string[] = Object.freeze([
  'entry_type', 'date_start', 'date_end', 'is_ongoing', 'activity_status',
]);

/** A value that says nothing: absent, unset, false, or the empty string. */
function isNegativeOrUnset(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (value === false) return true;
  if (typeof value === 'string') return value.trim().length === 0 || value === NOT_STATED || value === 'not_stated';
  return false;
}

/**
 * Does this fact earn a row? A positive value always does; a negative or unset one does so only for a field the
 * applicant is being asked to correct.
 */
export function factIsShown(fact: LedgerFactLike): boolean {
  const field = typeof fact.field === 'string' ? fact.field : '';
  if (ALWAYS_SHOWN_FIELDS.includes(field)) return true;
  return !isNegativeOrUnset(fact.value);
}

/**
 * The CURRENT fact per field, in first-appearance order — through the SHARED resolver.
 *
 * `facts[]` is an APPEND-ONLY AUDIT TRAIL (§2.8: "edits are stored with an audit trail and never silently
 * overwritten"), so a field the applicant has edited appears twice — the original `extracted_span` fact and the
 * `user_supplied` one that replaced it. `terminalFactsByField` resolves that LAST-WINS, and it is the same
 * function the rubric engine's `factIdFor` and the PATCH route's `replaces_span` lookup now call: round 1 had
 * three implementations of this rule and one of them (the engine's) was first-wins.
 */
export function currentFacts(entry: LedgerEntryLike): LedgerFactLike[] {
  const facts = Array.isArray(entry.facts) ? (entry.facts as LedgerFactLike[]) : [];
  return terminalFactsByField(
    facts.filter((f): f is LedgerFactLike & { field: string } => typeof f?.field === 'string'),
  );
}

/* ============================================================================================================== *
 * THE PANEL
 * ============================================================================================================== */

export interface FactLedgerPanelProps {
  /** Ledger entries as `GET /api/fact-ledger/:cv_id` returned them — already in document order (round 2). */
  entries: readonly LedgerEntryLike[];
  /**
   * `confirm` — the §3 screen, pre-read, ledger-only corrections, proceed control shown.
   * `read`    — Layer 1 inside the surface; corrections recompute.
   */
  mode: 'confirm' | 'read';
  /** The §1.5 precondition state. `precondition === null` disables every control (`CS-EDIT-DISABLED`). */
  state: CorrectionState;
  /** The parent runs the PATCH and dispatches the outcome; this panel never talks to the network. */
  onCorrect: (c: Correction) => void;
  /** `confirm` mode only — §3's skip/proceed control. */
  onProceed?: () => void;
  /** Entry ids in the read's own order, so Layer 1 agrees with Layers 2 and 4. */
  orderBy?: readonly string[];
  /**
   * R5.7b fix — `read` mode only: what the ENGINE read from this entry (its facets, descriptors, confidence line and
   * known-fact / phrasing recommendations), rendered inside the entry's card between its state notices and its facts
   * disclosure. The legacy read card carries none of it (Claude+Sol adjudicated under owner delegation 2026-08-22:
   * facets are reported on the facts screen, not the legacy read card). `index` is the entry's position in this
   * panel's order (0 carries the §3.0 facet framing).
   */
  entryReport?: (entry_id: string, index: number) => ReactNode;
}

export function FactLedgerPanel({
  entries, mode, state, onCorrect, onProceed, orderBy, entryReport,
}: FactLedgerPanelProps) {
  const ordered = orderEntries(entries, orderBy);
  const editable = canSubmit(state);
  return (
    <section
      // ONE COMPONENT, TWO MODES, TWO NAMES (accessibility audit A11Y-1 + A11Y-2, round 3). The audit found that
      // §3's confirmation screen and Layer 1 of the read were INDISTINGUISHABLE to a screen-reader user: same
      // markup, no heading, nothing announced. They are different steps with different stakes — one is a
      // pre-review checking step the applicant is expected to act on, the other is part of a finished review —
      // so each names itself.
      aria-labelledby={layerHeadingId(mode === 'confirm' ? 'CS-SECTION-CONFIRM' : 'CS-SECTION-LEDGER')}
      style={SECTION_STYLE}
      data-testid={mode === 'confirm' ? 'rubric-confirm-screen' : 'rubric-layer-ledger'}
      data-ledger-mode={mode}
    >
      <LayerHeading id={mode === 'confirm' ? 'CS-SECTION-CONFIRM' : 'CS-SECTION-LEDGER'} />
      {/* §3'S SCREEN SAYS WHAT IT IS AND WHAT IS BEING ASKED (A11Y-2). Visible, because the audit's finding was
          not only an accessibility one — nothing on this screen explained the step to anybody, and the only clue
          was a button reading "Looks right — run the review", which describes the button rather than the page.
          `role="status"` so it is also ANNOUNCED on arrival: this screen is reached by a state transition, not a
          navigation, so there is no page load for a screen reader to announce on the applicant's behalf.
          It promises nothing the screen does not do — proceeding writes NOTHING, and anything the applicant did
          not act on stays `extracted`. */}
      {mode === 'confirm' && (
        <p style={BODY_STYLE} role="status" data-testid="rubric-confirm-intro">
          <Copy id="CS-CONFIRM-SCREEN-INTRO" />
        </p>
      )}
      {/* §5's conflict notice. It STAYS until the applicant's own retry settles — the recovery is "re-fetch, then
          the user retries", and taking the notice away the moment the re-fetch lands would hide the first half of
          that sequence from the person who has to perform the second. */}
      {state.conflict && (
        <p style={NOTICE_STYLE} role="alert" data-testid="rubric-correct-conflict">
          <Copy id="CS-CORRECT-CONFLICT" />
        </p>
      )}
      {/* No token ⇒ no write (§1.5). Said out loud, with the registered sentence, rather than by silently
          rendering dead buttons. */}
      {!editable && (
        <p style={NOTICE_STYLE} role="status" data-testid="rubric-edit-disabled">
          <Copy id="CS-EDIT-DISABLED" />
        </p>
      )}
      {state.failed && (
        <p style={NOTICE_STYLE} role="alert" data-testid="rubric-correction-failed">
          <Copy id="CS-ERR-READ-UNAVAILABLE" />
        </p>
      )}

      <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {ordered.map((entry, i) => (
          <EntryCard
            key={String(entry.entry_id)}
            entry={entry}
            // EXPANDED on the confirmation screen, COLLAPSED in the standalone read panel (see the entry-card
            // block below for the decision).
            defaultOpen={mode === 'confirm'}
            editable={editable}
            rejected={state.rejected}
            onCorrect={onCorrect}
            report={mode === 'read' && entryReport ? entryReport(String(entry.entry_id), i) : null}
          />
        ))}
      </ol>

      {/* §3 — SKIPPABLE, NEVER HIDDEN. The screen always rendered above; this is the way out of it, and it writes
          nothing: facts the applicant did not act on stay `extracted`, which is §2.8's own meaning for "the user
          has not acted on this". Bulk-confirming them here would put words in their mouth — `user_confirmed`
          RAISES confidence (§2.8) and unlocks hard language (§8.4), on the strength of a click past a screen. */}
      {mode === 'confirm' && onProceed && (
        <button
          type="button"
          className="btn btn-primary"
          onClick={onProceed}
          data-testid="rubric-confirm-proceed"
        >
          <Copy id="CS-CONFIRM-ENTRY-CTA" />
        </button>
      )}
    </section>
  );
}

/** Document order, taken from the read when the caller has one; otherwise the ledger's own order, unchanged. */
function orderEntries(
  entries: readonly LedgerEntryLike[],
  orderBy: readonly string[] | undefined,
): LedgerEntryLike[] {
  const withIds = entries.filter((e) => str(e.entry_id) !== null);
  if (!orderBy || orderBy.length === 0) return [...withIds];
  const rank = new Map(orderBy.map((id, i) => [id, i]));
  // Entries the read does not mention keep their ledger order, AFTER the ones it does. Nothing is dropped: an
  // entry the engine did not assess is still an entry the applicant can correct. Since round 2 both orders are
  // DERIVED from the same document positions server-side, so this is agreement rather than imposition.
  const known = withIds.filter((e) => rank.has(String(e.entry_id)));
  const unknown = withIds.filter((e) => !rank.has(String(e.entry_id)));
  known.sort((a, b) => rank.get(String(a.entry_id))! - rank.get(String(b.entry_id))!);
  return [...known, ...unknown];
}

/* ============================================================================================================== *
 * ONE ENTRY — ITS IDENTITY, ITS STATE, AND ITS FACTS BEHIND A DISCLOSURE (R5.7)
 *
 * WHAT R5.7 CHANGED, AND WHY (owner direction 2026-08-22; live smoke). Layer 1 used to be a standalone wall —
 * every fact of every entry, open, at the top of the review — and the legacy read's per-entry cards were what the
 * owner asked to keep. So an entry's facts now live INSIDE its card, behind a `<details>` disclosure whose
 * `<summary>` is the registered `CS-SECTION-LEDGER` ("Facts read from your CV"), COLLAPSED BY DEFAULT in the read.
 * Collapsed, the card still shows what a reader needs without a click: the entry's identity (`EntryLabel`), its
 * §2.8 confirmation-state chip, the excluded/escalated notice and the extraction-uncertain notice. The facts and
 * EVERY correction control (edit, per-signal flag, entry flag, confirm, undo) are inside the disclosure — a
 * closed `<details>` keeps its content out of the tab order natively, with no ARIA of our own (T-A11Y-1).
 *
 * THE §3 CONFIRMATION SCREEN STARTS EXPANDED — a decision, recorded here. The screen exists to have the facts
 * checked; hiding them behind a click on the very step that asks for them would defeat it. The disclosure is
 * still offered, so an entry the applicant has finished checking can be folded away.
 *
 * THE DISCLOSURE IS FOCUS-TARGETABLE BY ID (`ledgerEntryDomId`): Layer 8's per-entry confidence line links to it
 * ("Go to this entry's facts"), opens it, and moves focus to its summary.
 * ============================================================================================================== */

/** The DOM id of an entry's facts disclosure — a SYSTEM id (§1.5 rule 2), the target of the confidence link. */
export function ledgerEntryDomId(entry_id: string): string {
  return `rubric-ledger-entry-${entry_id}`;
}

/**
 * One card of the standalone panel: identity, state, facts-disclosure. The read surface composes the SAME three
 * pieces inside its own richer entry card (`EntryCards.tsx`), with the facets, flags, confidence line and
 * recommendations between the state line and the disclosure.
 */
function EntryCard({
  entry,
  defaultOpen,
  editable,
  rejected,
  onCorrect,
  report,
}: {
  entry: LedgerEntryLike;
  defaultOpen: boolean;
  editable: boolean;
  rejected: CorrectionState['rejected'];
  onCorrect: (c: Correction) => void;
  /** R5.7b fix — the engine's per-entry report (read mode), between the state notices and the facts disclosure. */
  report?: ReactNode;
}) {
  const entryId = String(entry.entry_id);
  const stateName = typeof entry.confirmation_state === 'string' ? entry.confirmation_state : 'extracted';
  const heading = entryHeadingOf(entry);
  return (
    <li
      style={ENTRY_CARD_STYLE}
      data-entry-id={entryId}
      data-confirmation-state={stateName}
      // VISIBLY EXCLUDED (§4.1). Not `hidden` and not removed: the applicant has to be able to see the entry they
      // flagged, and to undo it. Muted, marked in the DOM, and stripped of its fact-level correction controls,
      // because there is nothing to correct on an entry that is out of the computation entirely.
      data-excluded={stateName === 'user_flagged_wrong' ? 'true' : undefined}
    >
      <div style={CARD_HEAD_STYLE}>
        <EntryLabel heading={heading} as="h3" style={ENTRY_TITLE_STYLE} />
        <StateChip stateName={stateName} />
      </div>
      <EntryStateNotices entry={entry} />
      {report}
      <EntryFactsDisclosure
        entry={entry}
        heading={heading}
        defaultOpen={defaultOpen}
        editable={editable}
        rejected={rejected}
        onCorrect={onCorrect}
      />
    </li>
  );
}

/**
 * The notices that stay VISIBLE while the facts are collapsed: extraction trouble, and the §8.4 escalation of a
 * flagged-wrong entry. Both are statements about the entry's state, which is exactly what a collapsed card must
 * still say.
 */
export function EntryStateNotices({ entry }: { entry: LedgerEntryLike }) {
  const stateName = typeof entry.confirmation_state === 'string' ? entry.confirmation_state : 'extracted';
  return (
    <>
      {entry.extraction_uncertain === true && (
        <p style={NOTICE_STYLE} data-testid="ledger-extraction-uncertain">
          <Copy id="CS-FLAG-EXTRACTION-UNCERTAIN" />
        </p>
      )}
      {stateName === 'user_flagged_wrong' && (
        <p style={NOTICE_STYLE} data-testid="ledger-flag-escalated">
          <Copy id="CS-FLAG-ESCALATED" />
        </p>
      )}
    </>
  );
}

/**
 * §2.8's confirmation state, as a chip.
 *
 * NOT a `BandChip` and NOT `data-rubric-band`, deliberately: a confirmation state is not a band, and putting it
 * inside T-UNIFORM-1's `[data-rubric-band]` sweep would make the uniform-typography contract compare a fact's
 * review status against a domain's documentation band as though they were the same kind of value.
 */
export function StateChip({ stateName }: { stateName: string }) {
  const id = STATE_CHIP[stateName];
  if (id === undefined) return null;
  return (
    <p style={{ ...MUTED_STYLE, margin: 0, whiteSpace: 'nowrap' }} data-fact-state={stateName} data-testid="ledger-state-chip">
      <Copy id={id} />
    </p>
  );
}

/**
 * THE FACTS DISCLOSURE — Layer 1 for one entry: every current fact with its typed value, its source reveal and its
 * editor, plus the entry-level controls (confirm / flag / undo), behind a `<details>`.
 *
 * `<details>`, deliberately, like `EvidenceReveal`: keyboard-operable and screen-reader-announced with no JavaScript
 * and no ARIA of our own (T-A11Y-1); closed, its content is out of the tab order. `open` is UNCONTROLLED — set once
 * from `defaultOpen`; the element owns its state after that, which is what lets the confidence link open it
 * through the DOM without fighting React.
 *
 * THE SUMMARY HAS A UNIQUE ACCESSIBLE NAME: the registered `CS-SECTION-LEDGER` plus the entry's own label,
 * visually hidden — the same disambiguation rule every repeated control on this surface follows, never an
 * invented word and never applicant text in an attribute.
 */
export function EntryFactsDisclosure({
  report,
  entry,
  heading,
  defaultOpen,
  editable,
  rejected,
  onCorrect,
}: {
  /**
   * 2026-08-23 (Sol review) — the entry's ENGINE REPORT, rendered INSIDE this disclosure.
   *
   * It used to be a SIBLING of this element, which meant the facets, descriptors, confidence line and
   * recommendations all sat exposed beneath the entry while the disclosure was shut — the "raw database" the
   * owner objected to, still on screen, just under a closed control. Passing it in is what makes "details are
   * one click away" true in the DOM rather than only in the summary's label.
   */
  report?: ReactNode;
  entry: LedgerEntryLike;
  heading: EntryHeading | null;
  defaultOpen: boolean;
  editable: boolean;
  rejected: CorrectionState['rejected'];
  onCorrect: (c: Correction) => void;
}) {
  const entryId = String(entry.entry_id);
  const stateName = typeof entry.confirmation_state === 'string' ? entry.confirmation_state : 'extracted';
  const excluded = stateName === 'user_flagged_wrong';
  const facts = currentFacts(entry);
  const context = heading === null ? null : <EntryLabel heading={heading} as="span" />;
  // THE TOGGLE'S STATE-SPECIFIC NAME (R5.8 verify-round-2, blocker for public ON). `<details>` stays UNCONTROLLED —
  // `open` is set once from `defaultOpen` and the element owns it — but the summary's accessible name must say which
  // way the control goes. `onToggle` fires on every change of the `open` attribute, including the confidence link's
  // DOM-side open, so this mirror can never disagree with the element. The verb is the registered CS-ENTRY-EXPAND /
  // CS-ENTRY-COLLAPSE pair (`rubricCopy.ts`: "the verb plus the entry's own label, visually hidden"); the visible
  // text stays the layer name the owner froze (CS-SECTION-LEDGER).
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <details
      id={ledgerEntryDomId(entryId)}
      className="rubric-entry-facts"
      style={FACTS_DETAILS_STYLE}
      open={defaultOpen}
      onToggle={(e) => setIsOpen((e.currentTarget as HTMLDetailsElement).open)}
      data-testid="rubric-entry-facts"
    >
      <summary style={FACTS_SUMMARY_STYLE}>
        <Copy id="CS-SECTION-LEDGER" />
        <ScreenReaderOnly>
          {' '}
          <Copy id={isOpen ? 'CS-ENTRY-COLLAPSE' : 'CS-ENTRY-EXPAND'} />
          {context !== null && (
            <>
              {' '}
              {context}
            </>
          )}
        </ScreenReaderOnly>
      </summary>
      {excluded ? (
        <div style={{ marginTop: 'var(--space-2)' }}>
          <ControlButton
            testid="ledger-unflag-entry"
            copyId="CS-FLAG-WRONG-UNDO"
            context={context}
            disabled={!editable}
            onClick={() => onCorrect({ op: 'unflag_entry', entryId })}
          />
        </div>
      ) : (
        <div style={{ marginTop: 'var(--space-2)' }}>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {facts.filter(factIsShown).map((f) => (
              <FactRow
                key={String(f.fact_id)}
                entryId={entryId}
                fact={f}
                editable={editable}
                rejected={rejected}
                onCorrect={onCorrect}
              />
            ))}
          </ul>
          {/* §5 step 1's THIRD correction class, which round 1 had no control for. Entry-scoped, and the label
              says so — `confirmation_state` is a column on the entry and the route accepts nothing smaller. */}
          {stateName !== 'user_confirmed' && (
            <ControlButton
              testid="ledger-confirm-entry"
              copyId="CS-CONFIRM-ENTRY-ACTION"
              context={context}
              disabled={!editable}
              onClick={() => onCorrect({ op: 'confirm_entry', entryId })}
            />
          )}
          {/* The ENTRY-level "this isn't right" — §2.8's `user_flagged_wrong`, which excludes the whole entry.
              Deliberately distinct from the per-signal-fact flag: §2.2a G6's whole point is that "a user who tells
              us we misread 'led' cannot be made to retract the setting we read correctly on the same line". The
              two controls share a registered label and are disambiguated for a screen reader by the registered
              field label and the applicant's own words. */}
          <ControlButton
            testid="ledger-flag-entry"
            copyId="CS-FLAG-WRONG-ACTION"
            context={context}
            disabled={!editable}
            onClick={() => onCorrect({ op: 'flag_entry', entryId })}
          />
        </div>
      )}
      {report}
    </details>
  );
}

/* ============================================================================================================== *
 * ONE FACT — ITS LABEL, ITS TYPED VALUE, ITS EVIDENCE, ITS EDITOR
 * ============================================================================================================== */

/**
 * The applicant-facing text behind a fact IF the surface may claim it came from the CV.
 *
 * TWO CASES, AND ONLY TWO (finding 4). An `extracted_span` fact's own quoted span is source material by
 * definition. A `user_supplied` fact's `replaces_span` is the wording it replaced, which is also source material.
 * A `user_supplied` fact WITHOUT a `replaces_span` has neither — its value is something the applicant typed that
 * never appeared in their document — so it gets no reveal at all, because `CS-SHOW-SOURCE` ("Shown from your CV")
 * would be a false statement about where the words came from.
 */
function sourceEvidenceOf(fact: LedgerFactLike): string | null {
  if (fact.provenance === 'extracted_span') return str(fact.evidence_span?.quoted_text);
  if (fact.provenance === 'user_supplied') return str(fact.replaces_span?.quoted_text);
  return null;
}

/** THE TYPED VALUE (§4.1). Every branch resolves to a registered string or to the applicant's own words. */
function FactValue({ field, fact, presentation }: {
  field: string;
  fact: LedgerFactLike;
  presentation: FieldPresentation;
}) {
  const value = fact.value;
  // §2.1 rule 2 — absence is a STATUS, rendered as such, and never as zero, blank, or "lacking".
  if (value === NOT_STATED || value === null) return <Copy id="CS-VAL-NOT-STATED" />;

  switch (presentation.kind) {
    case 'enum': {
      const id = enumLabelFor(field, value);
      // An unregistered member renders NOTHING rather than its raw token: printing
      // `supervised_trainee_postgrad` at the applicant is the internal-vocabulary leak §0 forbids.
      return id === null ? null : <Copy id={id} />;
    }
    case 'boolean':
      if (typeof value !== 'boolean') return null;
      return <Copy id={value ? 'CS-VAL-YES' : 'CS-VAL-NO'} />;
    case 'date_partial':
      return typeof value === 'string' ? <Copy id="CS-VALUE-DATE" slots={{ date: value }} /> : null;
    case 'integer':
    case 'number':
      return typeof value === 'number' ? <Copy id="CS-VALUE-COUNT" slots={{ count: String(value) }} /> : null;
    case 'text': {
      // THE PROVENANCE CHOOSES THE RENDERER, which is the whole point of §1.2b's typed content model.
      // `extracted_span`/`user_supplied` values ARE the applicant's own words and render verbatim, exempt from
      // the banned-output sweep. A `derived` or `closed_world` string is something this SYSTEM computed from
      // other facts; it renders through the registered single-slot template and is scanned as system copy,
      // because rendering it as `applicant_verbatim` would claim a provenance it does not have — the same false
      // claim the evidence-reveal fix closes one screenful up.
      const text = applicantTextOf(fact);
      if (text !== null) return <ApplicantText text={text} />;
      return typeof value === 'string' ? <Copy id="CS-VALUE-TEXT" slots={{ text: value }} /> : null;
    }
    case 'opaque':
      // §2.2/§4.2/§11.2 — an anonymous distinctness token. Equality comparison only; never display text.
      return null;
  }
}

/** The control's string form of the current value, for seeding an editor. */
function editableStringOf(fact: LedgerFactLike, presentation: FieldPresentation): string {
  const value = fact.value;
  if (value === NOT_STATED || value === null || value === undefined) return NOT_STATED;
  if (presentation.kind === 'text') return applicantTextOf(fact) ?? String(value);
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return String(value);
}

function FactRow({
  entryId,
  fact,
  editable,
  rejected,
  onCorrect,
}: {
  entryId: string;
  fact: LedgerFactLike;
  editable: boolean;
  rejected: CorrectionState['rejected'];
  onCorrect: (c: Correction) => void;
}) {
  const [editing, setEditing] = useState(false);
  const field = typeof fact.field === 'string' ? fact.field : '';
  const factId = str(fact.fact_id);
  const presentation = presentationFor(field);
  // A FIELD THIS BUILD HAS NO REGISTERED VOCABULARY FOR RENDERS NOTHING. With A.5-P registered that set is empty
  // for every field the extractor mints (`factLabels.test.ts` asserts it), so in practice this means "a newer
  // backend sent a field this bundle predates" — and the honest answer to that is the same one this surface gives
  // to any content it cannot resolve: no row, rather than an internal token.
  if (presentation === null || factId === null) return null;
  // The opaque distinctness token is not display text and is not editable (§2.2/§4.2/§11.2).
  if (presentation.kind === 'opaque') return null;

  const edited = fact.provenance === 'user_supplied';
  const flagged = fact.user_flagged_wrong === true;
  const isSignal = field.startsWith(SIGNAL_PREFIX);
  const canEdit = UI_EDITABLE_FIELDS.includes(field);
  const source = sourceEvidenceOf(fact);
  const label = <Copy id={presentation.labelId} />;

  return (
    <li style={{ margin: '0 0 var(--space-3)' }} data-fact-field={field} data-fact-id={factId}>
      <p style={BODY_STYLE}>
        {/* §4.1 — the field's registered NAME, then its TYPED VALUE. Round 1 rendered only prose, so a date, a
            count, a boolean or an enum rendered nothing at all. */}
        <span style={MUTED_STYLE} data-testid="ledger-fact-label">{label}</span>
        {' '}
        <span data-testid="ledger-fact-value">
          <FactValue field={field} fact={fact} presentation={presentation} />
        </span>
      </p>

      {/* §4.1 — an EDITED fact carries its own state label beside the value, so "this is what you told us" is
          visible without opening anything. */}
      {edited && (
        <p style={MUTED_STYLE} data-testid="ledger-fact-edited">
          <Copy id="CS-FACT-EDITED" />
        </p>
      )}
      {flagged && (
        <p style={MUTED_STYLE} data-testid="ledger-fact-flagged">
          <Copy id="CS-FACT-FLAGGED" />
        </p>
      )}

      {/* §4.1's `CS-SHOW-SOURCE` affordance — ONLY where there is genuine source material to show. */}
      {source !== null && (
        // ALWAYS `kind: 'span'` here, and that is a property of `sourceEvidenceOf` rather than an assumption:
        // it resolves ONLY an `extracted_span`'s own span and a `user_supplied` fact's `replaces_span` — the
        // original document wording, still available. Both are quoted clauses from the applicant's document, so
        // both stand alone; a stored VALUE never reaches this reveal at all (see `sourceEvidenceOf`).
        <EvidenceReveal evidence={[{ fact_id: factId, field, quoted_text: source, kind: 'span' }]} />
      )}

      {editing ? (
        <EditForm
          field={field}
          presentation={presentation}
          initial={editableStringOf(fact, presentation)}
          serverRejection={rejected?.field === field ? rejected.reason : null}
          label={label}
          onCancel={() => setEditing(false)}
          onSubmit={(value) => {
            setEditing(false);
            onCorrect({ op: 'edit', entryId, field, value });
          }}
        />
      ) : (
        <>
          {/* ONE CONTROL PER FACT AT A TIME: the "Edit" affordance is REPLACED by the form it opens, so a screen
              reader never meets two buttons named "Edit" in one fact. */}
          {canEdit && (
            <ControlButton
              testid="ledger-edit-fact"
              copyId="CS-CORRECT-ACTION"
              context={label}
              disabled={!editable}
              onClick={() => setEditing(true)}
            />
          )}
          {/* §2.2a G6 — the ATOMIC signal flag, the correction class T-FLAG-1b's band-movement contract runs
              through. Offered only for signal facts, because the server accepts `flag_facts` for nothing else: a
              date or a title is corrected through `user_edited`, which puts a replacement value in place of the
              one it removes rather than leaving an absence nobody stated. Already-flagged facts offer no control:
              there is no unflag operation in the API, and the entry-level undo is the honest recovery. */}
          {isSignal && !flagged && (
            <ControlButton
              testid="ledger-flag-fact"
              copyId="CS-FLAG-WRONG-ACTION"
              context={label}
              disabled={!editable}
              onClick={() => onCorrect({ op: 'flag_fact', entryId, factId })}
            />
          )}
        </>
      )}
    </li>
  );
}

/**
 * A correction control: a registered label, plus a UNIQUE screen-reader context.
 *
 * WHY THE HIDDEN SUFFIX. Several controls on one card legitimately share a registered label — every fact's "Edit",
 * the entry's and each signal's "This isn't right". The disambiguator is registered copy (the FIELD label) and,
 * where the control is about an entry, the applicant's own title rendered `applicant_verbatim` — never an
 * invented word, and never applicant text inside an `aria-label` attribute (§1.5 rule 2).
 */
function ControlButton({
  testid,
  copyId,
  context,
  disabled,
  onClick,
}: {
  testid: string;
  copyId: CopyStringId;
  context: ReactNode | null;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="btn btn-secondary"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
    >
      <Copy id={copyId} />
      {context !== null && (
        <ScreenReaderOnly>
          {' '}
          {context}
        </ScreenReaderOnly>
      )}
    </button>
  );
}

/* ============================================================================================================== *
 * THE EDITOR — ONE PER §2 VALUE KIND
 * ============================================================================================================== */

type Rejection = 'too_long' | 'invalid_characters' | 'invalid_value' | 'required';

/** Each refusal reason has its OWN registered sentence, because each has a different remedy (A.5-P.3). */
const REJECTION_COPY: Readonly<Record<Rejection, CopyStringId>> = Object.freeze({
  too_long: 'CS-EDIT-TOO-LONG',
  invalid_characters: 'CS-EDIT-INVALID-CHARACTERS',
  invalid_value: 'CS-EDIT-INVALID-VALUE',
  required: 'CS-EDIT-REQUIRED',
});

/**
 * The inline edit form (§1.5's client half), with a control chosen by the field's §2 VALUE KIND.
 *
 * `maxLength` comes from the SERVER'S OWN module, and `prepareEdit` runs the server's `inertString` before
 * anything is sent, so the value submitted is byte-identical to the value stored (NFC, newline-normalized per the
 * field's own rule, zero-width and bidi controls stripped) and the applicant is never shown one string and
 * charged for another.
 *
 * VALIDATION IS WORDED, VISIBLE, ASSOCIATED, AND ANNOUNCED (finding 8). Round 1 set `aria-invalid` and said
 * nothing, on the reasoning that Appendix A registered no refusal copy — which the review rejected outright.
 * The message now renders in a `role="alert"` region referenced by BOTH `aria-describedby` and
 * `aria-errormessage` (the former is universally supported; the latter is the correct modern relationship, and
 * naming both is what makes the association real rather than aspirational), and focus returns to the control
 * that has the problem so a keyboard user is not left reading an alert they cannot reach.
 */
function EditForm({
  field,
  presentation,
  initial,
  serverRejection,
  label,
  onSubmit,
  onCancel,
}: {
  field: string;
  presentation: FieldPresentation;
  initial: string;
  serverRejection: Rejection | null;
  label: ReactNode;
  onSubmit: (value: unknown) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(initial);
  const [localRejection, setLocalRejection] = useState<Rejection | null>(null);
  const controlRef = useRef<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null>(null);
  const errorId = useId();
  const rejection = localRejection ?? serverRejection;

  // Focus moves INTO the control that replaced the one the applicant just activated. Without this the focus ring
  // is left on a button that no longer exists and a keyboard user is returned to the top of the document.
  useEffect(() => {
    controlRef.current?.focus();
  }, []);

  // AND BACK TO IT WHEN A REFUSAL LANDS — including a refusal the SERVER reported, which arrives after the form
  // has already closed and reopened. An alert nobody can reach is not an accessible error message.
  useEffect(() => {
    if (rejection !== null) controlRef.current?.focus();
  }, [rejection]);

  function submit(e: FormEvent) {
    e.preventDefault();
    const typed = coerceEditValue(field, value);
    if (typed === '') {
      // §2.1 spells absence `not_stated`; an empty string is not a value. The remedy names the option that is.
      setLocalRejection('required');
      return;
    }
    const prepared = prepareEdit(field, typed);
    if (!prepared.ok) {
      setLocalRejection(prepared.reason);
      return;
    }
    setLocalRejection(null);
    // The NORMALIZED value is what goes up, and what the field would show if it stayed open.
    if (typeof prepared.value === 'string') setValue(prepared.value);
    onSubmit(prepared.value);
  }

  const common = {
    'aria-invalid': rejection !== null ? ('true' as const) : undefined,
    // BOTH relationships, deliberately — see the doc comment.
    'aria-describedby': rejection !== null ? errorId : undefined,
    'aria-errormessage': rejection !== null ? errorId : undefined,
    onKeyDown: (e: { key: string }) => {
      // ESC still cancels — it is the conventional key — but it is no longer the ONLY way out: `CS-EDIT-CANCEL`
      // is a real, visible control now, because an undiscoverable exit is not an exit.
      if (e.key === 'Escape') onCancel();
    },
    'data-testid': 'ledger-edit-input',
  };

  return (
    <form onSubmit={submit} data-testid="ledger-edit-form" data-edit-field={field}>
      <label style={{ display: 'block' }}>
        {/* The label is the registered FIELD name — round 1 had none and used the action string plus the
            applicant's value, which read as "Edit" on every control on the card. */}
        <span style={MUTED_STYLE}>{label}</span>
        <EditControl
          kind={presentation.kind}
          presentation={presentation}
          field={field}
          value={value}
          onChange={setValue}
          controlRef={controlRef}
          common={common}
        />
      </label>

      {rejection !== null && (
        <p
          id={errorId}
          role="alert"
          style={{ ...NOTICE_STYLE, marginTop: 'var(--space-1)' }}
          data-testid="ledger-edit-error"
        >
          {/* `CS-EDIT-REQUIRED` names the not-stated option by SLOT, filled from that option's own registered
              label, so the sentence and the control it points at cannot drift apart. */}
          <Copy
            id={REJECTION_COPY[rejection]}
            slots={rejection === 'required' ? { not_stated: NOT_STATED_LABEL } : undefined}
          />
        </p>
      )}

      <button type="submit" className="btn btn-secondary" data-testid="ledger-edit-submit">
        <Copy id="CS-EDIT-SAVE" />
        <ScreenReaderOnly>{' '}{label}</ScreenReaderOnly>
      </button>
      <button type="button" className="btn btn-secondary" onClick={onCancel} data-testid="ledger-edit-cancel">
        <Copy id="CS-EDIT-CANCEL" />
        <ScreenReaderOnly>{' '}{label}</ScreenReaderOnly>
      </button>
    </form>
  );
}

/* ------------------------------------------------------------------------------------------------------------ *
 * THE ONE PLACE THIS SURFACE RESOLVES A REGISTERED ID TO A PLAIN STRING
 *
 * `<option>` content must be text: an option element cannot host the `Copy` renderer's `<span data-copy-src=…>`,
 * because the browser renders an option's descendants as its label and any markup inside is either ignored or
 * rendered literally. So a select's option labels read `COPY[id]` directly.
 *
 * THAT IS STILL T-COPY-1-CLEAN, and this is why: the string is resolved FROM THE REGISTRY, by id, at the call
 * site — it is not authored here, and the id is stamped onto the option via `data-copy-id` so the DOM sweep can
 * still see which registered string produced the text. What is lost is the provenance WRAPPER, not the
 * provenance. `optionText` is the single funnel for it, so there is exactly one such place rather than one per
 * control.
 * ------------------------------------------------------------------------------------------------------------ */

function optionText(id: CopyStringId): string {
  return COPY[id];
}

/** The not-stated option's label, used BOTH as an option and as `CS-EDIT-REQUIRED`'s `{not_stated}` slot. */
const NOT_STATED_LABEL = optionText('CS-VAL-NOT-STATED');

/** The control itself, chosen by §2 value kind. */
function EditControl({
  kind,
  presentation,
  field,
  value,
  onChange,
  controlRef,
  common,
}: {
  kind: FieldPresentation['kind'];
  presentation: FieldPresentation;
  field: string;
  value: string;
  onChange: (v: string) => void;
  controlRef: React.MutableRefObject<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null>;
  common: Record<string, unknown>;
}) {
  const style = { display: 'block', width: '100%', maxWidth: '40rem' } as const;

  if (kind === 'enum' || kind === 'boolean') {
    const options: Array<{ v: string; id: CopyStringId }> = kind === 'boolean'
      ? [{ v: 'true', id: 'CS-VAL-YES' }, { v: 'false', id: 'CS-VAL-NO' }]
      // 2026-08-23 — THE EDITOR OFFERS ONLY WHAT THE DATABASE WILL STORE. `presentation.values` is TOTAL over the
      // enum so the facts screen can render a stored value of any kind; `not_editable_values` names the members
      // whose column CHECK has not been widened yet. Offering one would hand the applicant a control whose PATCH
      // the server refuses — see `STORABLE_ENTRY_TYPES` (workers/src/lib/factLedger.ts) and `T-ENUM-DB-1`.
      : Object.entries(editableValues(presentation)).map(([v, id]) => ({ v, id }));
    return (
      <select
        {...common}
        ref={(el) => { controlRef.current = el; }}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={style}
      >
        {presentation.allowsNotStated && (
          <option value={NOT_STATED} data-copy-id="CS-VAL-NOT-STATED">{NOT_STATED_LABEL}</option>
        )}
        {options.map((o) => (
          <option key={o.v} value={o.v} data-copy-id={o.id}>{optionText(o.id)}</option>
        ))}
      </select>
    );
  }

  if (presentation.multiline) {
    // THE §1.5 NEWLINE CONTRACT'S UI HALF. `responsibility_statement` is stored multi-line and normalized
    // multi-line; opening it in a single-line input showed the applicant a different string than the one stored,
    // which is the divergence the build review rejected.
    return (
      <textarea
        {...common}
        ref={(el) => { controlRef.current = el; }}
        value={value}
        required
        rows={4}
        maxLength={maxLengthFor(field)}
        onChange={(e) => onChange(e.target.value)}
        style={style}
      />
    );
  }

  if (kind === 'integer' || kind === 'number') {
    return (
      <input
        {...common}
        ref={(el) => { controlRef.current = el; }}
        type="number"
        inputMode="numeric"
        min={0}
        step={kind === 'integer' ? 1 : 'any'}
        value={value === NOT_STATED ? '' : value}
        onChange={(e) => onChange(e.target.value === '' ? NOT_STATED : e.target.value)}
        style={style}
      />
    );
  }

  if (kind === 'date_partial') {
    // §2.2: `YYYY` or `YYYY-MM`. PARTIAL DATES ARE FIRST-CLASS, so this is deliberately NOT `<input type="date">`,
    // which cannot express a year alone and would force a month nobody stated.
    return (
      <input
        {...common}
        ref={(el) => { controlRef.current = el; }}
        type="text"
        inputMode="numeric"
        pattern="\d{4}(-(0[1-9]|1[0-2]))?"
        value={value === NOT_STATED ? '' : value}
        onChange={(e) => onChange(e.target.value === '' ? NOT_STATED : e.target.value)}
        style={style}
      />
    );
  }

  return (
    <input
      {...common}
      ref={(el) => { controlRef.current = el; }}
      type="text"
      value={value}
      required
      maxLength={maxLengthFor(field)}
      onChange={(e) => onChange(e.target.value)}
      style={style}
    />
  );
}
